import { useEffect, useState } from "react";
import { ShieldCheck, Sparkles, Star, MapPin } from "lucide-react";
import { api, PROPERTY_IMAGES } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/lib/useSEO";

const VIKEY_LOCAL_KEY = "uSUTkJ4mqlVFevBiqoHStI61JzYYeG84x384GCmFqt4";

const BookingPage = () => {
  const [property, setProperty] = useState(null);
  const { t, lang } = useLang();

  useSEO({
    title: lang === "en" ? "Book your stay" : "Prenota il tuo soggiorno",
    description: lang === "en"
      ? "Check availability and book your stay at La dimora di Patty, holiday home in Castel di Sangro. Secure payment and instant confirmation."
      : "Verifica la disponibilità e prenota il tuo soggiorno alla dimora di Patty, casa vacanza a Castel di Sangro. Pagamento sicuro, conferma immediata.",
  });

  useEffect(() => {
    api.get("/property").then((r) => setProperty(r.data)).catch(() => {});
  }, []);

  const iframeSrc = `https://booking.vikey.it/?local_key=${VIKEY_LOCAL_KEY}&language=${lang === "en" ? "en" : "it"}`;

  return (
    <main data-testid="booking-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="text-center mb-12">
          <div className="overline text-accent gold-line-center mb-6">{t("booking.page_overline")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-tight">{t("booking.page_title")}</h1>
          <p className="text-muted-foreground max-w-xl mx-auto mt-5">
            {t("booking.page_subtitle")}
          </p>
        </Reveal>

        <Reveal>
          <div className="bg-card border border-border rounded-3xl overflow-hidden">
            <iframe
              key={lang}
              src={iframeSrc}
              title="Vikey Booking Engine — La dimora di Patty"
              className="w-full block"
              style={{ height: "1800px", border: "0" }}
              loading="lazy"
              allow="payment; clipboard-write"
              data-testid="vikey-booking-iframe"
            />
          </div>
        </Reveal>

        <Reveal className="mt-10 grid md:grid-cols-3 gap-4" delay={150}>
          <div className="bg-card border border-border rounded-2xl p-6 flex items-start gap-3">
            <ShieldCheck size={20} className="text-accent flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium">{t("booking.secure")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t("booking.secure_desc")}</div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6 flex items-start gap-3">
            <Sparkles size={20} className="text-accent flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium">{t("booking.instant")}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t("booking.instant_desc")}</div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6 flex items-start gap-3">
            <Star size={20} className="text-accent fill-accent flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium">{t("booking.rating")}</div>
              <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                <MapPin size={12} className="text-accent" />
                {t("booking.location")}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default BookingPage;
